/**
 * The postMessage contract between the review host page and the sandboxed
 * artifact iframe.
 *
 * The iframe runs with `sandbox="allow-scripts"` and no `allow-same-origin`, so
 * it has an opaque origin: it cannot read our Firebase token, cannot touch
 * `localStorage` (access *throws* there), and cannot call our API. Everything it
 * needs — persistence above all — is proxied through these messages, with the
 * host doing the privileged work.
 *
 * Payloads travel by structured clone, so `Date` values survive intact and
 * SitePing's records need no serialization on this hop. Only the host's
 * on-disk JSON converts them to ISO strings.
 */

export const FRAME_SOURCE = 'review-frame' as const;
export const HOST_SOURCE = 'review-host' as const;

/** Store methods the iframe may invoke on the host. Mirrors `SitepingStore`. */
export const STORE_METHODS = [
  'createFeedback',
  'getFeedbacks',
  'findByClientId',
  'updateFeedback',
  'deleteFeedback',
  'deleteAllFeedbacks',
] as const;

export type StoreMethod = (typeof STORE_METHODS)[number];

/**
 * Error kinds the host can signal back. SitePing's `isStoreNotFound` and
 * friends match on a stable `code` field precisely because every consumer
 * bundles its own copy of core, so the iframe can reconstruct an equivalent
 * error without importing their classes.
 */
export const STORE_ERROR_CODES = {
  notFound: 'STORE_NOT_FOUND',
  duplicate: 'STORE_DUPLICATE',
  persistence: 'STORE_PERSISTENCE',
} as const;

// ── Frame → host ────────────────────────────────────────────────────────────

/** Content size on load and on every subsequent reflow. */
export interface FrameResizeMessage {
  source: typeof FRAME_SOURCE;
  type: 'resize';
  height: number;
  width: number;
  /**
   * Whether the document can scroll itself. False means the host must not cap
   * the frame, or the artifact's overflow rules would leave nothing able to
   * scroll and everything below the fold unreachable.
   */
  scrollable?: boolean;
}

/** An RPC call against the host-owned feedback store. */
export interface FrameStoreCallMessage {
  source: typeof FRAME_SOURCE;
  type: 'store-call';
  id: number;
  method: StoreMethod;
  args: unknown[];
}

/** Opt-in escape hatch: a document storing its own semantic feedback. */
export interface FrameHostBridgeSaveMessage {
  source: typeof FRAME_SOURCE;
  type: 'host-bridge-save';
  payload: unknown;
}

export interface FrameHostBridgeLoadMessage {
  source: typeof FRAME_SOURCE;
  type: 'host-bridge-load';
  id: number;
}

/**
 * A workspace path the document reached for at runtime, for the host to sign.
 *
 * Asset references written in markup are rewritten before the document is ever
 * handed over, but one fetched from script is only a string until it runs, and
 * a relative one resolves against the *host* page from in here. The frame
 * cannot sign anything itself — that needs the API and a token, neither of
 * which an opaque origin has — so it asks.
 */
export interface FrameResolveUrlMessage {
  source: typeof FRAME_SOURCE;
  type: 'resolve-url';
  id: number;
  path: string;
}

export type FrameMessage =
  | FrameResizeMessage
  | FrameStoreCallMessage
  | FrameHostBridgeSaveMessage
  | FrameHostBridgeLoadMessage
  | FrameResolveUrlMessage;

// ── Host → frame ────────────────────────────────────────────────────────────

export interface HostStoreResultMessage {
  source: typeof HOST_SOURCE;
  type: 'store-result';
  id: number;
  ok: boolean;
  value?: unknown;
  error?: { message: string; code?: string };
}

export interface HostBridgeValueMessage {
  source: typeof HOST_SOURCE;
  type: 'host-bridge-value';
  id: number;
  value: unknown;
}

/** A signed URL for the requested path, or `null` if there is no such file. */
export interface HostResolveUrlResultMessage {
  source: typeof HOST_SOURCE;
  type: 'resolve-url-result';
  id: number;
  url: string | null;
}

export type HostMessage =
  | HostStoreResultMessage
  | HostBridgeValueMessage
  | HostResolveUrlResultMessage;

/**
 * Cap on a single inbound message. The document may contain model-authored JS,
 * so an accidental (or hostile) unbounded payload must not reach the workspace.
 */
export const MAX_MESSAGE_BYTES = 256 * 1024;

/**
 * The cap for a message carrying a crop.
 *
 * A comment arrives with a base64 JPEG of the boxed region attached, which alone
 * runs past the general cap. The bytes never reach the feedback JSON — the host
 * pulls them out and uploads the crop as its own workspace file — so this bounds
 * one in-flight image, not anything durable.
 */
export const MAX_SCREENSHOT_MESSAGE_BYTES = 12 * 1024 * 1024;

/** Store methods whose payload may legitimately carry a crop. */
export const SCREENSHOT_BEARING_METHODS = new Set(['createFeedback', 'updateFeedback']);

export function isFrameMessage(data: unknown): data is FrameMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === FRAME_SOURCE &&
    typeof (data as { type?: unknown }).type === 'string'
  );
}
